import { Component, OnInit, ViewEncapsulation, HostListener, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { take, finalize } from 'rxjs';

import { RequestType, PaymentType } from '@app/shared/types';
import { PaymentMethodService, TcServiceService } from '@app/shared/services';
import { PaymentMethod } from '@app/shared/models';
import { ServicePublicList } from '@app/shared/services/service-public-list';
import { PaymentMethodNew } from '@app/shared/models/payment-method-new';
import { Router } from '@angular/router';
import { InlineAlertTypes } from '@app/shared/types/inline-alert-types';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MousePressDirective } from '@app/shared/directives/mouse-press.directive';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { InlineAlertComponent } from '../../inline-alert/inline-alert.component';
import { TableLoadingComponent } from '../../table-loading/table-loading.component';

@Component({
    selector: 'tc-request-payment-type',
    imports: [
        CommonModule,
        TranslateModule,
        MatExpansionModule,
        MatButtonModule,
        MousePressDirective,
        MatRadioModule,
        FormsModule,
        InlineAlertComponent,
        TableLoadingComponent
    ],
    templateUrl: './request-payment-type.component.html',
    styleUrls: ['./request-payment-type.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class RequestPaymentTypeComponent implements OnInit, AfterViewInit {
  @Output() validationRequirementEmit = new EventEmitter<number>();
  @Output() selectedPaymentMethodEmit = new EventEmitter<PaymentMethod>();
  @Output() selectedTcWalletPaymentMethodEmit = new EventEmitter<PaymentMethod>();
  @Output() selectedServiceEmit = new EventEmitter<ServicePublicList>();
  @Output() selectedCryptoEmit = new EventEmitter<ServicePublicList>();
  @Input() requestType: RequestType;

  // data
  paymentMethod: PaymentMethodNew;
  paymentMethodList: PaymentMethod[];
  services: ServicePublicList[];
  subServices: ServicePublicList[];
  selectedService: ServicePublicList;

  // types
  paymentType = PaymentType;
  requestTypes = RequestType;
  inlineAlertTypes = InlineAlertTypes;

  // boolean
  isLoading: boolean;
  servicesIsLoading: boolean;
  subServiceIsLoading: boolean;

  // style
  disableExpansionPanel = true;

  constructor(
    private paymentMethodService: PaymentMethodService,
    private tcServiceService: TcServiceService,
    private router: Router
  ) { }


  ngOnInit() {
    this.fetchPaymentMethod();
  }
  ngAfterViewInit(): void {
    this.onResize(null);
  }


  selectPaymentMethod(paymentMethod: PaymentMethod): void {
    if (!this.paymentMethod.localPayemnts.enable) {
      return;
    }
    this.validationRequirementEmit.emit(this.paymentMethod.localPayemnts.validationRequirement);
    this.selectedPaymentMethodEmit.emit(paymentMethod);
  }

  selectTcWalletPaymentMethod(paymentMethod: PaymentMethod): void {
    if (!this.paymentMethod.tcWallet.enable) {
      return;
    }
    this.validationRequirementEmit.emit(this.paymentMethod.tcWallet.validationRequirement);
    this.selectedTcWalletPaymentMethodEmit.emit(paymentMethod);
  }

  openRequests(middleRouteName: string): void {
    let url;
    switch (this.requestType) {

      case RequestType.Deposit:
        url = `/deposit/${middleRouteName}/list`;
        this.router.navigate([url]);
        break;

      case RequestType.Withdrawal:
        url = `/withdrawal/${middleRouteName}/list`;
        this.router.navigate([url]);
        break;

      case RequestType.Send:
        url = `/send/${middleRouteName}/list`;
        this.router.navigate([url]);
        break;

      case RequestType.Exchange:
        url = `/exchange/${middleRouteName}/list`;
        this.router.navigate([url]);
    }
  }

  /*******************************************************************
    services
  *******************************************************************/

  clickEMoney(service: ServicePublicList): void {
    this.selectedService = null;
    if (!this.paymentMethod.eMoney.enable) {
      return;
    }
    if (service.hasSubService) {
      this.fetchSubServices(service.serviceId);
      this.hideAllSubServices();
      // show sub services of this card
      service.showSub = true;
      return;
    }
    this.selectedService = service;
    this.selectEmoney();
  }

  selectEmoney(): void {
    if (!this.selectedService) {
      return;
    }
    this.validationRequirementEmit.emit(this.paymentMethod.eMoney.validationRequirement);
    this.selectedServiceEmit.emit(this.selectedService);
  }

  clickCrypto(service: ServicePublicList): void {

    this.selectedService = null;
    if (!this.paymentMethod.crypto.enable) {
      return;
    }
    if (service.hasSubService) {
      this.fetchSubServices(service.serviceId);
      this.hideAllSubServices();
      // show sub services of this card
      service.showSub = true;
      return;
    }
    this.selectedService = service;
    this.selectCrypto();
  }

  selectCrypto(): void {
    if (!this.selectedService) {
      return;
    }
    this.validationRequirementEmit.emit(this.paymentMethod.crypto.validationRequirement);
    this.selectedCryptoEmit.emit(this.selectedService);
  }

  hideSubService(service: ServicePublicList): void {
    service['showSub'] = false;
    this.selectedService = null;
  }

  /*******************************************************************
  Fetch
  *******************************************************************/
  private fetchPaymentMethod(): void {
    this.isLoading = true;
    this.paymentMethodService.getAll(this.requestType)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        paymentMethodNew => {
          this.paymentMethod = paymentMethodNew;
        }
      );
  }

  private fetchSubServices(serviceId: number): void {
    this.servicesIsLoading = true;
    this.subServices = null;
    this.tcServiceService.getSubServices(this.requestType, serviceId)
      .pipe(
        take(1),
        finalize(() => {
          this.servicesIsLoading = false;
        })
      )
      .subscribe(
        res => {
          this.subServices = res;
        }
      );
  }

  private hideAllSubServices(): void {
    if (this.paymentMethod.eMoney) {
      this.paymentMethod.eMoney.methods.forEach(el => {
        try {
          el['showSub'] = false;
        } catch (ex) { }
      });
    }

    if (this.paymentMethod.crypto) {
      this.paymentMethod.crypto.methods.forEach(el => {
        try {
          el['showSub'] = false;
        } catch (ex) { }
      });
    }

    if (this.paymentMethod.service) {
      this.paymentMethod.service.methods.forEach(el => {
        try {
          el['showSub'] = false;
        } catch (ex) { }
      });
    }
  }

  /*******************************************************************
  Styles
  *******************************************************************/

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (window.innerWidth < 768) {
      this.disableExpansionPanel = false;
    } else {
      this.disableExpansionPanel = true;

    }
  }

}
